import '@fortawesome/fontawesome-free/css/all.min.css';
import React, { useEffect, useRef, useState } from "react";
import "./SideBar.css";
import { getUserProfile, getUserPicture } from "services/userProfileService";
import profile1 from "assets/images/profile-img.png"; 
import ConfirmModal from "components/Common/ConfirmModal";
import LoadingSpinner from 'components/Common/Loading-Spinner';
import Pagination from 'components/Common/Pagination';
import { 
  getPolicyEmergingOutcomes,
  createPolicyEmergingOutcome,
  updatePolicyEmergingOutcome,
  deletePolicyEmergingOutcome
} from "services/policyEmergingOutcomesService";

const Sidebar = ({ policy, onClose, canvasTab = "updates", alert }) => {
  const [activeTab, setActiveTab] = useState(canvasTab);
  const [userProfile, setUserProfile] = useState(null);
  const [userPhotos, setUserPhotos] = useState({});
  const [loading, setLoading] = useState(true);
  const [emergingOutcomes, setEmergingOutcomes] = useState([]);
  const [newOutcomeDescription, setNewOutcomeDescription] = useState("");
  const [editingOutcome, setEditingOutcome] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [confirmModal, setConfirmModal] = useState(false);
  const [outcomeToDelete, setOutcomeToDelete] = useState(null);
  const sidebarRef = useRef(null);

  // Pagination parameters
  const entriesPerPage = 10;

  // Fetch user profile and picture
  useEffect(() => {
    const userId = 1; // Hardcoded user ID for now; replace with dynamic value if necessary
    loadUserProfile(userId);
  }, []);

  // Load emerging outcomes when policy changes or when updates tab is active
  useEffect(() => {
    if (policy?.id && activeTab === "updates") {
      // Reset to first page when policy changes
      setCurrentPage(1);
      loadEmergingOutcomes();
    }
  }, [policy, activeTab]);
  
  // Separate effect to handle page changes
  useEffect(() => {
    if (policy?.id && activeTab === "updates") {
      loadEmergingOutcomes();
    }
  }, [currentPage]);

  const loadEmergingOutcomes = async () => {
    if (!policy?.id) return;
    
    try {
      setLoading(true);
      const offset = (currentPage - 1) * entriesPerPage;
      const response = await getPolicyEmergingOutcomes(policy.id, offset, entriesPerPage);
      
      // Set the emerging outcomes directly, no appending
      setEmergingOutcomes(response.data || []);      
      setTotalEntries(response.count);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching emerging outcomes:", error);
      alert("Loading emerging outcomes", false);
      setLoading(false);
    }
  };

  const loadUserProfile = async (id) => {
    try {
      const profileData = await getUserProfile(id); // Fetch profile
      setUserProfile(profileData);

      if (profileData?.id) {
        loadUserPicture(profileData.id);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setLoading(false);
    }
  };

  const loadUserPicture = async (user_id) => {
    try {
      const picture = await getUserPicture(user_id);
      const pictureUrl = URL.createObjectURL(picture);
      setUserPhotos((prevPhotos) => ({
        ...prevPhotos,
        [user_id]: pictureUrl
      }));
    } catch (err) {
      console.error("Error fetching user picture:", err);
    }
  };

  const handleCreateOutcome = async () => {
    if (!newOutcomeDescription.trim()) {
      alert("Description cannot be empty so", false);
      return;
    }

    try {
      const outcomeData = { description: newOutcomeDescription };
      await createPolicyEmergingOutcome(policy.id, outcomeData);
      setNewOutcomeDescription("");
      resetPagination(); // Reset to first page
      await loadEmergingOutcomes();
      alert("Emerging outcome addition", true);
    } catch (error) {
      console.error("Error creating emerging outcome:", error);
      alert("Creating emerging outcome", false);
    }
  };

  const handleUpdateOutcome = async () => {
    if (!editDescription.trim()) {
      alert("Description cannot be empty so", false);
      return;
    }

    try {
      const outcomeData = { description: editDescription };
      await updatePolicyEmergingOutcome(policy.id, editingOutcome.id, outcomeData);
      setEditingOutcome(null);
      setEditDescription("");
      resetPagination(); // Reset to first page
      await loadEmergingOutcomes();
      alert("Emerging outcome update", true);
    } catch (error) {
      console.error("Error updating emerging outcome:", error);
      alert("Updating emerging outcome", false);
    }
  };

  const handleDeleteOutcome = (outcomeId) => {
    setOutcomeToDelete(outcomeId);
    setConfirmModal(true);
  };
  
  const confirmDeleteOutcome = async () => {
    try {
      await deletePolicyEmergingOutcome(policy.id, outcomeToDelete);
      setOutcomeToDelete(null);
      resetPagination(); // Reset to first page
      await loadEmergingOutcomes();
      alert("Emerging outcome deletion", true);
    } catch (error) {
      console.error("Error deleting emerging outcome:", error);
      alert("Deleting emerging outcome", false);
    }
  };
  
  const toggleConfirmModal = () => {
    setConfirmModal(!confirmModal);
  };

  const startEditOutcome = (outcome) => {
    setEditingOutcome(outcome);
    setEditDescription(outcome.description);
  };

  const cancelEdit = () => {
    setEditingOutcome(null);
    setEditDescription("");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const resetPagination = () => {
    setCurrentPage(1);
  };

  const renderBulletPoints = (text, user, timestamp, userPhoto) => {
    return (
      <ul>
        {text
          .split("\\n")
          .filter((item) => item.trim() !== "")
          .map((item, index) => (
            <li key={index}>
              {item}
              <div className="meta-info">
                <div className="user-timestamp">
                  <div className="user-info">
                    <img
                      className="profile-photo"
                      src={userPhoto}
                      alt="Profile"
                      onError={(e) => { e.target.onerror = null; e.target.src = profile1; }} // Fallback on error
                    />
                    <div className="name-timestamp">
                      <span className="user-name">{user}</span>
                      <span className="timestamp">{new Date(timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
      </ul>
    );
  };

  const getUserInfo = (item) => {
    const user = item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : `${item.created_by.first_name} ${item.created_by.last_name}`;

    const timestamp = item.updated_by ? item.updated_at : item.created_at;
    const userId = item.updated_by ? item.updated_by.id : item.created_by.id;
    const userPhoto = userPhotos[userId] || profile1; // Fallback if no photo

    return { user, timestamp, userPhoto };
  };

  const renderEmergingOutcomes = () => {
    if (loading && emergingOutcomes.length === 0) {
      return <LoadingSpinner/>;
    }

    if (emergingOutcomes.length === 0) {
      return <p>No emerging outcomes available</p>;
    }

    return (
      <div className="space-y-4 mt-4">
        {emergingOutcomes.map(outcome => (
          <div key={outcome.id} className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
            {editingOutcome && editingOutcome.id === outcome.id ? (
              <div className="space-y-3">
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full min-h-[80px] p-2 border border-gray-300 rounded-md resize-y text-sm"
                />
                <div className="flex justify-end space-x-2">
                  <button 
                    onClick={handleUpdateOutcome} 
                    className="common-button"
                  >
                    Save
                  </button>
                  <button 
                    onClick={cancelEdit} 
                    className="common-outlined-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-800 text-sm leading-relaxed mb-3">{outcome.description}</p>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <img
                      className="w-8 h-8 rounded-full border border-green-700 mr-2"
                      src={userPhotos[outcome.created_by?.id] || profile1}
                      alt="Profile"
                      onError={(e) => { e.target.onerror = null; e.target.src = profile1; }}
                    />
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold">
                        {outcome.created_by?.first_name} {outcome.created_by?.last_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(outcome.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button 
                      onClick={() => startEditOutcome(outcome)} 
                      className="common-button"
                      aria-label="Edit"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      onClick={() => handleDeleteOutcome(outcome.id)} 
                      className="common-cancel-button"
                      aria-label="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        
        <div className="mt-4 relative">
          {totalEntries > 0 && (
            <Pagination
              currentPage={currentPage}
              goToPage={handlePageChange}
              totalEntries={totalEntries}
              entriesPerPage={entriesPerPage}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="sidebar" ref={sidebarRef}>
      <div className="sidebar-title-container">
        <div className="sidebar-title">
          <h3>{policy?.name || "Policy Information"}</h3>
          <button className="close-icon" onClick={onClose}>
            <i className="fas fa-times-circle"></i>
          </button>
        </div>
      </div>
      <div className="sidebar-content-container">
        <div className="sidebar-tabs">
          <button
            className={activeTab === "updates" ? "active tab-button" : "tab-button"}
            onClick={() => setActiveTab("updates")}
          >
            Updates & Emerging Outcomes
          </button>
          <button
            className={activeTab === "details" ? "active tab-button" : "tab-button"}
            onClick={() => setActiveTab("details")}
          >
            Policy Details
          </button>
        </div>
        <div className="sidebar-content">
          {activeTab === "updates" && (
            <div className="tab-content">
              <div className="my-3">
                <div className="bg-gray-50 p-2 rounded-lg">
                  <textarea
                    value={newOutcomeDescription}
                    onChange={(e) => setNewOutcomeDescription(e.target.value)}
                    placeholder="Enter description of emerging outcome..."
                    className="w-full min-h-[80px] p-2 border border-gray-300 rounded-md resize-y text-sm"
                  />
                  <button 
                    onClick={handleCreateOutcome} 
                    className="common-button ml-auto my-0"
                    disabled={loading}
                  >
                    {loading ? "Adding..." : "Add"}
                  </button>
                </div>
                {renderEmergingOutcomes()}
              </div>
            </div>
          )}
          {activeTab === "details" && (
            <div className="tab-content">
              {policy.details ? (
                renderBulletPoints(
                  policy.details,
                  getUserInfo(policy).user,
                  getUserInfo(policy).timestamp,
                  getUserInfo(policy).userPhoto
                )
              ) : (
                <p>No details available</p>
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmModal 
        show={confirmModal}
        toggle={toggleConfirmModal}
        onConfirmClick={confirmDeleteOutcome}
        role="deleteItem"
      />
    </div>
  );
};

export default Sidebar;